import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Provider } from 'react-redux';
import { store } from './store/store';

// --- Vapi & Context Imports ---
import { VapiProvider } from './contexts/VapiContext'; 

// Existing Imports
import LoginPage from './pages/LoginPage';
import DashboardLayout from './components/Layout/DashboardLayout';
import DashboardPage from './pages/DashboardPage';
import CommunityRegistryPage from './pages/CommunityRegistryPage';
import ConversationsPage from './pages/ConversationsPage';
import ContactsPage from './pages/ContactsPage';
import CampaignsPage from './pages/CampaignsPage';
import PreChatFormsPage from './pages/PreChatFormsPage';
import AgentCapacityPage from './pages/AgentCapacityPage';
import AgentsPage from './pages/AgentsPage';
import LabelsPage from './pages/LabelsPage';
import TeamsPage from './pages/TeamsPage';
import SlackIntegrationPage from './pages/SlackIntegrationPage';
import InboxesPage from './pages/InboxesPage';
import IntegrationsPage from './pages/IntegrationsPage';
import BusinessHoursPage from './pages/BusinessHoursPage';
import CannedResponsesPage from './pages/CannedResponsesPage';
import SettingsPage from './pages/SettingsPage';
import DocumentationPage from './pages/DocumentationPage';
import ReportsPage from './pages/ReportsPage';
import AutomationRulesPage from './pages/Automation/AutomationRulesPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import RoleManagementPage from './pages/RoleManagementPage';
import CSATAnalyticsPage from './pages/CSATAnalyticsPage';
import HelpPage from './pages/HelpPage';
import APIDocumentationPage from './pages/APIDocumentationPage';
import ChangelogPage from './pages/ChangelogPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MyProfilePage from './pages/MyProfilePage';
import WhatsAppTemplatesPage from './pages/WhatsAppTemplatesPage';
import AvatarCustomizer from './pages/AvatarCustomizer';
import { useAppSelector, useAppDispatch } from './store/hooks';
import { KeyboardShortcutsProvider } from './contexts/KeyboardShortcutsContext';
import { fetchUserPermissions } from './store/slices/authSlice'; // 14/11/2025 - User Access Management Module Phase 5.2.1
import EmailTemplatesPage from './pages/EmailTemplatesPage'; // 09/02/2026 - Added for email template management
import AIStudioPage from './pages/AIStudio'; // Phase 1: Embed RAGFlow UI

const PJS = '"Plus Jakarta Sans", ui-sans-serif, system-ui, -apple-system, sans-serif';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1f93ff',
    },
    secondary: {
      main: '#f50057',
    },
  },
  typography: {
    fontFamily: PJS,
    h1: { fontFamily: PJS, letterSpacing: '-0.02em' },
    h2: { fontFamily: PJS, letterSpacing: '-0.02em' },
    h3: { fontFamily: PJS, letterSpacing: '-0.02em' },
    h4: { fontFamily: PJS, letterSpacing: '-0.015em' },
    h5: { fontFamily: PJS, letterSpacing: '-0.01em' },
    h6: { fontFamily: PJS, letterSpacing: '-0.01em' },
    subtitle1: { fontFamily: PJS },
    subtitle2: { fontFamily: PJS },
    body1: { fontFamily: PJS },
    body2: { fontFamily: PJS },
    button: { fontFamily: PJS, textTransform: 'none', fontWeight: 500 },
    caption: { fontFamily: PJS },
    overline: { fontFamily: PJS },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: PJS,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      },
    },
  },
});

const getRouterBasename = (): string | undefined => {
  if (process.env.NODE_ENV !== 'production') {
    return undefined;
  }

  const rawBasename = process.env.REACT_APP_BASENAME?.trim() || '';
  if (!rawBasename) {
    return undefined;
  }

  const normalized = rawBasename.startsWith('/') ? rawBasename : `/${rawBasename}`;
  return normalized.endsWith('/') && normalized !== '/' ? normalized.slice(0, -1) : normalized;
};

function AppRoutes() {
  const dispatch = useAppDispatch();
  const { user, token, permissionsLoaded } = useAppSelector(state => state.auth);
  const isAuthenticated = !!token && !!user;

  useEffect(() => {
    if (isAuthenticated && !permissionsLoaded) {
      console.log('🔐 Fetching user permissions...');
      dispatch(fetchUserPermissions());
    }
  }, [isAuthenticated, permissionsLoaded, dispatch]);

  return (
    <>
      <Routes>
        <Route
          path="/login"
          element={!isAuthenticated ? <LoginPage /> : <Navigate to="/dashboard" />}
        />
        <Route
          path="/forgot-password"
          element={!isAuthenticated ? <ForgotPasswordPage /> : <Navigate to="/dashboard" />}
        />
        <Route
          path="/reset-password"
          element={!isAuthenticated ? <ResetPasswordPage /> : <Navigate to="/dashboard" />}
        />
        <Route
          path="/dashboard/*" 
          element={
            isAuthenticated ? (
              <DashboardLayout>
                <Routes>
                  <Route path="" element={<DashboardPage />} />
                  <Route path="community-registry" element={<CommunityRegistryPage />} />
                  <Route path="conversations" element={<ConversationsPage />} />
                  <Route path="conversations/:id" element={<ConversationsPage />} />
                  <Route path="contacts" element={<ContactsPage />} />
                  <Route path="inboxes" element={<InboxesPage />} />
                  <Route path="integrations" element={<IntegrationsPage />} />
                  <Route path="ai-studio" element={<AIStudioPage />} />
                  <Route path="ai-studio/:section" element={<AIStudioPage />} />
                  <Route path="reports" element={<ReportsPage />} />
                  <Route path="automation" element={<AutomationRulesPage />} />
                  <Route path="labels" element={<LabelsPage />} />
                  <Route path="teams" element={<TeamsPage />} />
                  <Route path="agents" element={<AgentsPage />} />
                  <Route path="roles" element={<RoleManagementPage />} />
                  <Route path="canned-responses" element={<CannedResponsesPage />} />
                  <Route path="campaigns" element={<CampaignsPage />} />
                  <Route path="pre-chat-forms" element={<PreChatFormsPage />} />
                  <Route path="agent-capacity" element={<AgentCapacityPage />} />
                  <Route path="slack-integration" element={<SlackIntegrationPage />} />
                  <Route path="business-hours" element={<BusinessHoursPage />} />
                  <Route path="knowledge-base" element={<KnowledgeBasePage />} />
                  <Route path="csat-analytics" element={<CSATAnalyticsPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="documentation" element={<DocumentationPage />} />
                  <Route path="help" element={<HelpPage />} />
                  <Route path="api-docs" element={<APIDocumentationPage />} />
                  <Route path="changelog" element={<ChangelogPage />} />
                  <Route path="my-profile" element={<MyProfilePage />} />
                  <Route path="whatsapp-templates" element={<WhatsAppTemplatesPage />} />
                  <Route path="email-templates" element={<EmailTemplatesPage />} />
                  <Route path="avatar-builder" element={<AvatarCustomizer />} />
                </Routes>
              </DashboardLayout>
            ) : (
              <Navigate to="/login" />
            )
          } 
        />
        <Route path="/" element={<Navigate to="/dashboard" />} />
      </Routes>
    </>
  );
}

function App() {
  const routerBasename = getRouterBasename();

  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {/* 2. VapiProvider wraps the entire Router so the calling 
            logic is never interrupted by route changes.
        */}
        <VapiProvider>
          <Router basename={routerBasename}>
            <KeyboardShortcutsProvider>
              <AppRoutes />
            </KeyboardShortcutsProvider>
          </Router>
        </VapiProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;